import { useEffect, useState } from "react";
import { filterLanguages, filterRegions, filterSorts, filterYears } from "@/mocks/filters";
import { allWorks } from "@/utils/media";

export interface FilterState {
  region: string;
  year: string;
  language: string;
  genre: string;
  sort: string;
}

const genreOptions = ["全部", ...Array.from(new Set(allWorks.flatMap((w) => w.genres)))];

interface FilterBarProps {
  value: FilterState;
  onChange: (patch: Partial<FilterState>) => void;
  onReset: () => void;
  resultCount: number;
}

interface SelectProps {
  label: string;
  icon: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}

function SelectField({ label, icon, value, options, onChange }: SelectProps) {
  return (
    <label className="flex flex-1 flex-col gap-1">
      <span className="flex items-center gap-1 text-xs font-medium text-foreground-500">
        <i className={icon} />
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="cursor-pointer rounded-md border border-background-300/70 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const toOptions = (list: string[]) => list.map((v) => ({ value: v, label: v }));

export default function FilterBar({ value, onChange, onReset, resultCount }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const selects = (
    <>
      <SelectField
        label="地区"
        icon="ri-earth-line"
        value={value.region}
        options={toOptions(filterRegions)}
        onChange={(v) => onChange({ region: v })}
      />
      <SelectField
        label="年份"
        icon="ri-calendar-line"
        value={value.year}
        options={toOptions(filterYears)}
        onChange={(v) => onChange({ year: v })}
      />
      <SelectField
        label="语言"
        icon="ri-translate-2"
        value={value.language}
        options={toOptions(filterLanguages)}
        onChange={(v) => onChange({ language: v })}
      />
      <SelectField
        label="题材"
        icon="ri-price-tag-3-line"
        value={value.genre}
        options={toOptions(genreOptions)}
        onChange={(v) => onChange({ genre: v })}
      />
      <SelectField
        label="排序"
        icon="ri-sort-desc"
        value={value.sort}
        options={filterSorts.map((s) => ({ value: s.value, label: s.label }))}
        onChange={(v) => onChange({ sort: v })}
      />
    </>
  );

  return (
    <>
      <div className="hidden flex-wrap items-end gap-3 rounded-lg border border-background-200/70 bg-background-100/60 p-4 lg:flex">
        {selects}
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300/70 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
        >
          <i className="ri-refresh-line" />
          重置
        </button>
        <span className="ml-auto whitespace-nowrap self-center text-xs text-foreground-500">
          共 <strong className="text-primary-700">{resultCount}</strong> 部作品
        </span>
      </div>

      <div className="flex items-center gap-3 lg:hidden">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800"
        >
          <i className="ri-equalizer-line" />
          筛选 / 排序
        </button>
        <span className="text-xs text-foreground-500">
          共 <strong className="text-primary-700">{resultCount}</strong> 部
        </span>
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-end lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-foreground-950/50"
          />
          <div className="relative w-full rounded-t-2xl bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-950">筛选与排序</h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-background-300/70 text-foreground-700"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="flex flex-col gap-3">{selects}</div>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={onReset}
                className="flex flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300/70 py-2.5 text-sm font-medium text-foreground-700"
              >
                <i className="ri-refresh-line" />
                重置
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex flex-1 items-center justify-center whitespace-nowrap rounded-md bg-primary-500 py-2.5 text-sm font-semibold text-background-50"
              >
                查看结果（{resultCount}）
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}